import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DeleteForever,
  Edit as EditIcon,
} from '@mui/icons-material';
import MuiChip from 'app/design/uiComponents/MuiChip';
import moment from 'moment';
import { Leg } from './types';

interface LegsSectionProps {
  legs: Leg[];
  expandedSections: {
    legs: boolean;
  };
  onSectionToggle: (section: 'legs') => void;
  onClearLegs: () => void;
  onLegDeleteConfirm: (legId: string) => void;
  onAddEditClick: () => void;
  getCurrentLTP: (leg: Leg) => number;
  handleShowUnderlyingChange: (show: boolean) => void;
  showUnderlying?: boolean;
}

const LegsSection: React.FC<LegsSectionProps> = ({
  legs,
  expandedSections,
  onSectionToggle,
  onClearLegs,
  onLegDeleteConfirm,
  onAddEditClick,
  getCurrentLTP,
  handleShowUnderlyingChange,
  showUnderlying = false
}) => {
  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        sx={{ pb: 1 }}
      >
        <Typography variant="h6" fontWeight="bold">
          Positions
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {/* <FormControlLabel
              control={
                <Switch
                  checked={showUnderlying}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleShowUnderlyingChange(checked);
                  }}
                  size="small"
                />
              }
              label="Show Underlying"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: '#666',
                },
              }}
            /> */}
          <IconButton
            size="small"
            onClick={() => onSectionToggle('legs')}
          >
            {expandedSections.legs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Action Buttons Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        mb={1}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            cursor: 'pointer',
            color: '#d32f2f',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={() => {
            onClearLegs();
          }}
        >
          <DeleteIcon fontSize="small" />
          <Typography variant="body2" color="#d32f2f">
            Clear
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              cursor: 'pointer',
              color: '#1976d2',
              '&:hover': { opacity: 0.8 },
            }}
            onClick={onAddEditClick}
          >
            <EditIcon fontSize="small" />
            <Typography variant="body2" color="#1976d2">
              Add/Edit
            </Typography>
          </Box>
        </Box>
      </Box>

      <Collapse in={expandedSections.legs}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>B/S</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>Strike</TableCell>
                <TableCell>LTP</TableCell>
                <TableCell
                  align="center"
                  sx={{ width: '120px', minWidth: '120px' }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {legs.map(leg => {
                const currentLTP = getCurrentLTP(leg);

                return (
                  <TableRow key={leg.id}>
                    <TableCell>
                      <MuiChip
                        label={leg.direction === 'long' ? 'B' : 'S'}
                        variant="filled"
                        size="singleLetter"
                        color={
                          leg.direction === 'long'
                            ? 'purplelight'
                            : 'infolight'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {leg.expiry && moment(leg.expiry).format('DD MMM YY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {leg.strike}{' '}
                        {leg.contractType
                          ? leg.contractType.toUpperCase()
                          : ''}{' '}
                        x {leg.lots}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {currentLTP.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: '120px', minWidth: '120px' }}
                    >
                      <Tooltip title="Delete Leg" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onLegDeleteConfirm(leg.id)}
                          sx={{
                            color: 'error.main',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'error.light',
                              color: 'error.contrastText',
                            },
                          }}
                        >
                          <DeleteForever fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};

export default LegsSection; 